/**
 * Server answers translated into the values the reports use.
 *
 * Kept in one place so the password screen and the second-factor screen cannot
 * drift apart: a failure counted as `rate_limited` on one and as something else
 * on the other would make the two impossible to compare. Takes plain shapes
 * rather than the auth service's types, so this folder has no runtime
 * dependency on the feature code.
 */
import type {
  AnalyticsEventParams,
  AnalyticsGuardedResult,
  AnalyticsLoginFailure,
  AnalyticsMfaMethod,
  AnalyticsRouteKind,
} from './analytics.types';

/** The parts of an `HttpErrorResponse` a failure is judged by. */
export interface FailedRequest {
  status?: number;
  error?: { status?: unknown } | null;
}

/**
 * Why a sign-in request was refused, read the same way the screens read it to
 * choose what to say.
 */
export function loginFailureFor(
  stage: 'password' | 'mfa',
  err: FailedRequest | null | undefined,
): AnalyticsLoginFailure {
  const reason = err?.error?.status;
  if (reason === 'ACCOUNT_LOCKED') return 'account_locked';
  if (reason === 'MFA_EXPIRED') return 'challenge_expired';

  const status = err?.status ?? 0;
  if (status === 429) return 'rate_limited';
  // A transport fault or a server fault says nothing about what was typed.
  if (status === 0 || status >= 500) return 'request_failed';

  if (stage === 'mfa') return 'invalid_code';
  return status === 401 ? 'invalid_credentials' : 'request_failed';
}

/**
 * Why a request behind reCAPTCHA was refused. The screens tell the same three
 * cases apart — the rate limit, a refused challenge, anything else — and so
 * does this.
 */
export function guardedFailureFor(
  status: number | undefined,
): Exclude<AnalyticsGuardedResult, 'sent'> {
  if (status === 429) return 'rate_limited';
  if (status === 403) return 'captcha_failed';
  return 'failed';
}

/** `TOTP` → `totp`, matching the casing of every other reported value. */
export function mfaMethodFor(method: 'TOTP' | 'EMAIL' | 'BACKUP'): AnalyticsMfaMethod {
  return method.toLowerCase() as AnalyticsMfaMethod;
}

/** `SEND_LIMIT` → `send_limit`, for the answer to a request for an emailed code. */
export function sendStatusFor(
  status: 'SENT' | 'COOLDOWN' | 'SEND_LIMIT',
): AnalyticsEventParams['mfa_code_requested']['send_status'] {
  return status.toLowerCase() as Lowercase<typeof status>;
}

/** `SINGLE_LAB` → `single_lab`. */
export function routeKindFor(
  kind: 'SINGLE_LAB' | 'AUTO_ROUTE' | 'PICKER',
): AnalyticsRouteKind {
  return kind.toLowerCase() as AnalyticsRouteKind;
}
